import React from 'react';
import { Tooltip, message } from 'antd';
import { SaveTwoTone, UnorderedListOutlined } from '@ant-design/icons';
import TagsSelect from './TagsSelect';
import PermissionsSwitch from './PermissionsSwitch';
import {
  USER_TYPES,
  GENERAL_PERMISSIONS,
  PERMISSIONS_TOGGLES,
  ROLES_DATA,
  USER_PERMISSIONS_ERRORS,
  DEFAULT_GLOBAL_PERMISSIONS,
} from '../constants';
import { User } from '../types';
import { AuthState } from '@auth0/auth0-react/dist/auth-state';
import { Button } from '../../../components';

type GlobalPermissionsProps = {
  usersData: User[];
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: (value: React.Key[]) => void;
  globalSelectedValue: string[];
  setGlobalSelectedValue: (value: string[]) => void;
  globalPermissions: Record<string, boolean>;
  setGlobalPermissions: (permissions: Record<string, boolean>) => void;
  selectedUsers: User[];
  selectedTags: string[] | null;
  setSelectedTags: (value: string[] | null) => void;
  setUserTypeModal: (value: boolean) => void;
  auth0User: AuthState['user'];
  isAdmin: boolean;
  handleAssignPermissions: (users: any) => void;
};

const GlobalPermissions: React.FC<GlobalPermissionsProps> = ({
  usersData,
  selectedRowKeys,
  setSelectedRowKeys,
  globalSelectedValue,
  setGlobalSelectedValue,
  globalPermissions,
  setGlobalPermissions,
  selectedUsers,
  selectedTags,
  setSelectedTags,
  setUserTypeModal,
  auth0User,
  isAdmin,
  handleAssignPermissions,
}) => {
  const SHOULD_DISPLAY_ICON_LIST =
    globalSelectedValue[0] === USER_TYPES.DIVISION ||
    globalSelectedValue[0] === USER_TYPES.LOCATION;

  const handleSelectUpdate = (value: string[]) => {
    setGlobalSelectedValue(value);
    setSelectedTags(null);
    setUserTypeModal(true);
    setGlobalPermissions(DEFAULT_GLOBAL_PERMISSIONS);
  };

  const includesCurrentUser = selectedUsers.some(
    (user) => user.uuid === auth0User?.sub,
  );

  const isCurrentUserActive = selectedUsers.some(
    (user) =>
      user.uuid === auth0User?.sub && user.roles.generalPermissions.active,
  );

  const areAllCorporate = selectedUsers.every(
    (user) => user.roles.selectedValue === USER_TYPES.CORPORATE.toUpperCase(),
  );

  const handlePermissionChange = (key: string, checkedValue: boolean) => {
    if (
      key === GENERAL_PERMISSIONS.ACTIVE &&
      includesCurrentUser &&
      isCurrentUserActive &&
      globalPermissions[key]
    ) {
      return message.error(USER_PERMISSIONS_ERRORS.ACTIVE_PERMISSIONS);
    }

    if (
      key === GENERAL_PERMISSIONS.ADMIN &&
      includesCurrentUser &&
      isAdmin &&
      globalPermissions[key]
    ) {
      return message.error(USER_PERMISSIONS_ERRORS.ADMIN_PERMISSIONS);
    }

    if (
      key === GENERAL_PERMISSIONS.ADMIN &&
      !areAllCorporate &&
      globalSelectedValue[0] !== USER_TYPES.CORPORATE &&
      checkedValue
    ) {
      return message.error(USER_PERMISSIONS_ERRORS.CORPORATE_PERMISSIONS);
    }

    setGlobalPermissions({
      ...globalPermissions,
      [key]: checkedValue,
    });
  };

  const handleBulkUpdate = () => {
    const selectedUsersList = usersData.filter((user) =>
      selectedRowKeys.includes(user.uuid),
    );

    const isCurrentUserIncluded = selectedUsersList.some(
      (user) => user.uuid === auth0User?.sub,
    );

    const doesSomeoneHaveActivePermissions = selectedUsersList.some(
      (user) => user.roles.generalPermissions.active,
    );

    const doesSomeoneHaveAdminPermissions = selectedUsersList.some(
      (user) => user.roles.generalPermissions.admin,
    );

    if (
      !globalPermissions[GENERAL_PERMISSIONS.ACTIVE] &&
      isCurrentUserIncluded &&
      doesSomeoneHaveActivePermissions
    ) {
      return message.error(USER_PERMISSIONS_ERRORS.ACTIVE_PERMISSIONS_BULK);
    }

    if (
      !globalPermissions[GENERAL_PERMISSIONS.ADMIN] &&
      isCurrentUserIncluded &&
      doesSomeoneHaveAdminPermissions
    ) {
      return message.error(USER_PERMISSIONS_ERRORS.ADMIN_PERMISSIONS_BULK);
    }

    const SHOULD_HAVE_TAGS =
      globalSelectedValue[0] === USER_TYPES.DIVISION ||
      globalSelectedValue[0] === USER_TYPES.LOCATION;

    if (SHOULD_HAVE_TAGS && !selectedTags?.length) {
      message.error(`Please select at least one ${globalSelectedValue[0]}(s)`);
      return;
    }

    const updatedUsers = selectedUsersList.map((user) => {
      const roles = user.roles;
      const { __typename, ...rest } = roles as {
        __typename: string;
        [key: string]: any;
      };

      return {
        ...rest,
        generalPermissions: globalPermissions,
        selectedValue: globalSelectedValue.length
          ? globalSelectedValue[0]?.toUpperCase()
          : rest.selectedValue,
        selectedTags: globalSelectedValue.length
          ? selectedTags
          : rest.selectedTags,
        userAuth0ID: user.uuid,
      };
    });

    handleAssignPermissions(updatedUsers);

    setSelectedRowKeys([]);
    setGlobalSelectedValue([]);
    setGlobalPermissions(DEFAULT_GLOBAL_PERMISSIONS);
  };

  return (
    <div className="w-full flex items-center gap-5">
      <section>
        <div className="w-full flex items-center justify-center gap-2.5">
          <TagsSelect
            rolesData={ROLES_DATA}
            selectedValue={globalSelectedValue}
            updateSelectValue={handleSelectUpdate}
          />
          {SHOULD_DISPLAY_ICON_LIST && (
            <Tooltip title="Edit list">
              <UnorderedListOutlined
                onClick={() => setUserTypeModal(true)}
                style={{ fontSize: '16px', color: '#437EF7' }}
                className="transition-transform duration-300 group-hover:scale-125 cursor-pointer"
              />
            </Tooltip>
          )}
        </div>
      </section>
      <div className="w-full flex items-center justify-center gap-2">
        {PERMISSIONS_TOGGLES.map(({ key, label }) => (
          <PermissionsSwitch
            key={key}
            uuid={key}
            permissionKey={key}
            label={label}
            checked={globalPermissions[key]}
            disabled={false}
            onChange={(checkedValue) =>
              handlePermissionChange(key, checkedValue)
            }
          />
        ))}
      </div>
      <section>
        <Button type="edit" onClick={handleBulkUpdate}>
          <Tooltip title="Save">
            <SaveTwoTone className="text-blue hover:text-white" />
          </Tooltip>
        </Button>
      </section>
    </div>
  );
};

export default GlobalPermissions;
